import { CreditCard, Database, Plug, Store } from 'lucide-react';

import { useShop } from '@/hooks/useShop';
import { useShopPath } from '@/hooks/useShopPath';
import { useChannels } from '@/hooks/useChannels';
import { useChannelDefinitions } from '@/hooks/useChannelDefinitions';
import { useInventoryPageSummary } from '@/hooks/useInventory';
import { getTitle } from '@/lib/channels';
import { SettingsCardList } from './components/SettingsCardList';

const integerFormat = new Intl.NumberFormat('en-GB');

function inventoryDetail(shop, summary) {
	const source = shop?.stockInventorySource;
	if (!source) return 'Not connected — connect your POS to start syncing';
	const label = source.providerLabel || source.pointOfSale || 'POS';
	const count = summary?.totalItems;
	if (Number.isFinite(count) && count > 0) {
		return `Connected via ${label} · ${integerFormat.format(count)} items`;
	}
	return `Connected via ${label}`;
}

function storefrontDetail(shop) {
	if (!shop) return 'Social links, return & delivery policies';
	const social = [shop.website, shop.facebook, shop.instagram, shop.twitter].filter(Boolean).length;
	if (!social) return 'Social links, return & delivery policies';
	return `${social} social ${social === 1 ? 'link' : 'links'} · return & delivery policies`;
}

// Match the Lovable design's signposting language ("Google, Meta, Storefront")
// rather than a count. Reading from definitions keeps the label canonical
// (matches what the user sees on the Channels page) and order-stable.
function channelsDetail(channels, definitions, channelsLoading, definitionsLoading) {
	if (channelsLoading || definitionsLoading) return 'Loading channels…';
	const live = channels.filter((c) => c.status === 'enabled');
	if (!live.length) return 'No channels live yet';
	const titles = live.map((c) => getTitle(c.type, definitions[c.type]) || c.type).slice(0, 3);
	const hidden = live.length - titles.length;
	return hidden > 0 ? `${titles.join(', ')} +${hidden}` : titles.join(', ');
}

// `shop.plan` is the canonical Unify subscription tier
// (`unify-1` / `unify-2` / `unify-3` / `unify-4`) — the seed sets a
// different slug per location so this differentiates correctly.
// `currentPlusPlan` is the legacy NearSt Plus subscription, identical
// across shops in dev seed, so it can't be used here.
function billingDetail(shop) {
	if (!shop?.plan) return 'Subscription, payment method';
	const formatted = shop.plan
		.split('-')
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(' ');
	return `${formatted} plan`;
}

export function StoreLanding() {
	const { shop } = useShop();
	const { shopPath } = useShopPath();
	const { channels, isLoading: channelsLoading } = useChannels(shop?.id);
	const { definitions, isLoading: definitionsLoading } = useChannelDefinitions();
	const { summary } = useInventoryPageSummary(shop?.id);

	const items = [
		{
			icon: Database,
			label: 'Inventory feed',
			detail: inventoryDetail(shop, summary),
			to: shopPath('/inventory/connection')
		},
		{
			icon: Store,
			label: 'Local Storefront info',
			detail: storefrontDetail(shop),
			to: shopPath('/settings/store/storefront')
		},
		{
			icon: Plug,
			label: 'Channels & integrations',
			detail: channelsDetail(channels, definitions, channelsLoading, definitionsLoading),
			to: shopPath('/channels')
		},
		{
			icon: CreditCard,
			label: 'Billing & plan',
			detail: billingDetail(shop),
			to: shopPath('/settings/store/billing')
		}
	];

	return <SettingsCardList items={items} />;
}
